import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../api/client";
import {
  RuntimeSettingItem,
  SystemStatusResponse,
  fetchSystemStatus,
  updateRuntimeSetting,
} from "../api/systemStatus";
import "./SystemStatusPage.css";

const REFRESH_INTERVAL_MS = 30_000;

function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return "—";
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} gün ${h} sa`;
  if (h > 0) return `${h} sa ${m} dk`;
  return `${m} dk`;
}

const SEVERITY_LABELS: Record<string, string> = {
  ok: "Sağlıklı",
  warn: "Dikkat",
  critical: "Kritik",
};

function SettingRow({
  setting,
  onSaved,
}: {
  setting: RuntimeSettingItem;
  onSaved: (settings: RuntimeSettingItem[]) => void;
}) {
  const [draft, setDraft] = useState(String(setting.value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(String(setting.value));
  }, [setting.value]);

  const dirty = Number(draft) !== setting.value;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await updateRuntimeSetting(setting.key, Number(draft));
      onSaved(res.settings);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="kp-sys__setting">
      <div className="kp-sys__setting-info">
        <strong>{setting.label}</strong>
        <span>{setting.description}</span>
        <small>
          Aralık: {setting.min}–{setting.max} {setting.unit}
          {setting.isDefault ? " · varsayılan değer kullanılıyor" : ""}
        </small>
        {error && <small className="kp-sys__setting-error">{error}</small>}
      </div>
      <div className="kp-sys__setting-controls">
        <input
          className="kp-input"
          type="number"
          min={setting.min}
          max={setting.max}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          className="kp-btn kp-btn--primary"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? "…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

export function SystemStatusPage() {
  const [status, setStatus] = useState<SystemStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await fetchSystemStatus();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sistem durumu alınamadı.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const handleSettingsSaved = useCallback((settings: RuntimeSettingItem[]) => {
    setStatus((prev) => (prev ? { ...prev, runtime_settings: settings } : prev));
  }, []);

  if (loading) {
    return <div className="kp-card kp-sys__loading">Sistem durumu yükleniyor…</div>;
  }
  if (error || !status) {
    return <div className="kp-card kp-sys__loading">{error ?? "Veri yok."}</div>;
  }

  const { db, app, security, migrations, observability, recommendations, runtime_settings: settings } = status;
  const dbPercent = db.size_percent;

  return (
    <div className="kp-sys">
      <div className="kp-sys__header">
        <p>
          Son güncelleme: {new Date(status.generated_at).toLocaleTimeString("tr-TR")} · 30 saniyede bir
          yenilenir. Metrikler uygulama yeniden başladığında sıfırlanır.
        </p>
      </div>

      <section className="kp-sys__cards" aria-label="Öneriler">
        {recommendations.map((card) => (
          <div key={card.id} className={`kp-card kp-sys__reco kp-sys__reco--${card.severity}`}>
            <div className="kp-sys__reco-head">
              <span className={`kp-sys__badge kp-sys__badge--${card.severity}`}>
                {SEVERITY_LABELS[card.severity] ?? card.severity}
              </span>
              <strong>{card.title}</strong>
            </div>
            <p>{card.message}</p>
            <p className="kp-sys__reco-action">Ne yapmalı: {card.action}</p>
          </div>
        ))}
      </section>

      <div className="kp-sys__grid">
        <section className="kp-card kp-sys__panel">
          <h2>Veritabanı</h2>
          <dl>
            <div>
              <dt>Bağlantı</dt>
              <dd>{db.ping.ok ? `Sağlıklı (${db.ping.latency_ms} ms)` : `HATA: ${db.ping.error ?? "?"}`}</dd>
            </div>
            <div>
              <dt>Havuz (uygulama)</dt>
              <dd>
                {db.pool.total}/{db.pool.max} bağlantı · {db.pool.idle} boşta · {db.pool.waiting} bekleyen
              </dd>
            </div>
            <div>
              <dt>Sunucu bağlantıları</dt>
              <dd>
                {db.server_connections
                  ? `${db.server_connections.total} toplam · ${db.server_connections.active} aktif`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Depolama</dt>
              <dd>
                {formatBytes(db.size_bytes)} / {formatBytes(db.size_limit_bytes)}
                {dbPercent !== null ? ` (%${dbPercent})` : ""}
              </dd>
            </div>
          </dl>
          {dbPercent !== null && (
            <div className="kp-sys__meter" role="img" aria-label={`Depolama %${dbPercent}`}>
              <div
                className={`kp-sys__meter-fill${dbPercent >= 80 ? " kp-sys__meter-fill--warn" : ""}`}
                style={{ width: `${Math.min(100, dbPercent)}%` }}
              />
            </div>
          )}
          <h3>En büyük tablolar</h3>
          <table className="kp-sys__table">
            <thead>
              <tr>
                <th>Tablo</th>
                <th>Boyut</th>
                <th>~Satır</th>
              </tr>
            </thead>
            <tbody>
              {db.top_tables.map((t) => (
                <tr key={t.table_name}>
                  <td>{t.table_name}</td>
                  <td>{formatBytes(t.bytes)}</td>
                  <td>{t.approx_rows.toLocaleString("tr-TR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="kp-card kp-sys__panel">
          <h2>Uygulama</h2>
          <dl>
            <div>
              <dt>Çalışma süresi</dt>
              <dd>{formatUptime(app.uptime_seconds)}</dd>
            </div>
            <div>
              <dt>İstek (son 5 dk)</dt>
              <dd>
                {app.http.requests_last_5m} istek · {app.http.requests_per_minute_5m} istek/dk
              </dd>
            </div>
            <div>
              <dt>Yanıt süresi</dt>
              <dd>
                ort {app.http.avg_duration_ms_5m} ms · p95 {app.http.p95_duration_ms} ms · en yavaş{" "}
                {app.http.max_duration_ms_5m} ms
              </dd>
            </div>
            <div>
              <dt>Hatalar (son 5 dk)</dt>
              <dd>
                {app.http.errors_5xx_last_5m} sunucu (5xx, %{app.http.error_rate_5xx_percent_5m}) ·{" "}
                {app.http.errors_4xx_last_5m} istemci (4xx)
              </dd>
            </div>
            <div>
              <dt>Event loop gecikmesi</dt>
              <dd>{app.http.event_loop_lag_ms} ms</dd>
            </div>
            <div>
              <dt>Bellek</dt>
              <dd>
                {app.memory_rss_mb} MB / {app.memory_limit_mb} MB (heap {app.memory_heap_used_mb} MB)
              </dd>
            </div>
            <div>
              <dt>Node</dt>
              <dd>{app.node_version}</dd>
            </div>
            <div>
              <dt>Uygulanan migration</dt>
              <dd>{migrations?.applied_count ?? 0}</dd>
            </div>
          </dl>

          {migrations?.recent?.length > 0 && (
            <>
              <h3>Son migration dosyaları</h3>
              <table className="kp-sys__table">
                <thead>
                  <tr>
                    <th>Dosya</th>
                    <th>Uygulama</th>
                  </tr>
                </thead>
                <tbody>
                  {migrations.recent.map((m) => (
                    <tr key={m.filename}>
                      <td>{m.filename}</td>
                      <td>{new Date(m.applied_at).toLocaleString("tr-TR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <h3>Gözlemlenebilirlik</h3>
          <dl>
            <div>
              <dt>Sentry</dt>
              <dd>{observability?.sentry_enabled ? "Aktif (SENTRY_DSN tanımlı)" : "Kapalı — Render'da SENTRY_DSN ekleyin"}</dd>
            </div>
            <div>
              <dt>Health (sığ)</dt>
              <dd>
                <code>{observability?.health_shallow_path ?? "/api/health"}</code>
              </dd>
            </div>
            <div>
              <dt>Health (DB dahil)</dt>
              <dd>
                <code>{observability?.health_deep_path ?? "/api/health?deep=true"}</code>
              </dd>
            </div>
            <div>
              <dt>Uptime izleme</dt>
              <dd>
                GitHub Actions → <strong>{observability?.uptime_github_workflow ?? "Uptime Health Check"}</strong>
                {" "}(secret: <code>UPTIME_HEALTH_URL</code>)
              </dd>
            </div>
          </dl>

          <h3>Güvenlik olayları (son 24 saat)</h3>
          {security.events_last_24h.length === 0 ? (
            <p className="kp-sys__empty">Kayıtlı güvenlik olayı yok.</p>
          ) : (
            <table className="kp-sys__table">
              <thead>
                <tr>
                  <th>Olay</th>
                  <th>Adet</th>
                </tr>
              </thead>
              <tbody>
                {security.events_last_24h.map((e) => (
                  <tr key={e.event_type}>
                    <td>{e.event_type}</td>
                    <td>{e.c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="kp-card kp-sys__panel">
        <h2>Çalışma Zamanı Ayarları</h2>
        <p className="kp-sys__hint">
          Bu ayarlar veritabanında saklanır ve <strong>yeniden deploy gerektirmeden</strong> en geç 30
          saniye içinde etkinleşir.
        </p>
        {settings.map((setting) => (
          <SettingRow key={setting.key} setting={setting} onSaved={handleSettingsSaved} />
        ))}
      </section>

      <section className="kp-card kp-sys__panel">
        <h2>Plan yükseltme bağlantıları</h2>
        <ul className="kp-sys__links">
          <li>
            <a href="https://dashboard.render.com" target="_blank" rel="noreferrer">
              Render Dashboard
            </a>{" "}
            — backend planını yükseltmek için (kursiyer-backend → Settings → Instance Type)
          </li>
          <li>
            <a href="https://console.neon.tech" target="_blank" rel="noreferrer">
              Neon Console
            </a>{" "}
            — veritabanı planı / depolama için (Project → Billing)
          </li>
          <li>
            Plan yükselttikten sonra <code>DB_SIZE_LIMIT_BYTES</code> ve <code>MEMORY_LIMIT_MB</code>{" "}
            env değişkenlerini yeni limitlere göre güncelleyin; bu ekrandaki yüzdeler ona göre hesaplanır.
          </li>
        </ul>
      </section>
    </div>
  );
}
